import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import styled from 'styled-components';

// Store
import AppState from '@webapp/store/state';

// Types
import User from '@common/types/user/user';

// Transalte
import { Translate } from 'react-localize-redux';

// Controllers
import UserAvatarInfo from '@components/basics/user-avatar-info';

// Components
import Flex from '@components/layout/flex';
import SeparatorLine from '@components/basics/separator-line';
import ProgressLine from '@components/upload/progress-line';
import NewMenu from '@components/new-menu';
import Typography from '@components/basics/typography';
import TransferIcon from '@components/basics/icons/transfer';

import { CatalogTabs } from '@webapp/pages/catalog';
import Button from '@components/actions/button';
import { StartImportCatalog } from '@webapp/store/import-catalog/action-creators';

interface CatalogTransferMenuProps extends Partial<User> {
  className?: string;
  isMobile?: boolean;
  artistId?: string;
  inProgress?: boolean;
  progress?: number;
  startImportCatalog: (artistId: string) => StartImportCatalog;
  section: CatalogTabs;
}

const mapStateToProps = (state: AppState) => ({
  avatar: state.artist.active?.avatar,
  fullName: state.artist.active?.name,
  isMobile: state.ui.mobile,
  inProgress: state.importCatalog.inProgress,
  progress: state.importCatalog.progress,
  artistId: state.artist.active?.id,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  startImportCatalog: (artistId: string) =>
    dispatch(new StartImportCatalog(artistId)),
});

export const CatalogTransferMenu: React.FC<CatalogTransferMenuProps> = ({
  className,
  avatar,
  fullName,
  artistId,
  inProgress,
  progress,
  startImportCatalog,
  isMobile,
}) => {
  const handleClick = () => {
    if (artistId) {
      startImportCatalog(artistId);
    }
  };

  return (
    <NewMenu
      className={className}
      button={
        <PillButton
          className={''}
          alignItems="center"
          justifyContent="space-between"
          gap={10}
        >
          <Typography className="menu-btn-title" weight="700">
            <Translate id="CATALOG.TRANSFER_MENU.TITLE" />
          </Typography>
          <TransferIcon />
        </PillButton>
      }
      menuId="CATALOG_TRANSFER_MENU"
      centeredPadding={!isMobile}
      topHeader={isMobile && <UserAvatarInfo src={avatar} name={fullName!} />}
      header={!isMobile && <UserAvatarInfo src={avatar} name={fullName!} />}
      footer={
        !inProgress ? (
          <Button small={true} onClick={handleClick}>
            <Translate id="CATALOG.TRANSFER_MENU.BUTTON.LABEL" />
          </Button>
        ) : (
          <ButtonWrapper>
            <Button small={true} disabled={true} height={50}>
              <Translate id="CATALOG.TRANSFER_MENU.BUTTON.LABEL.STARTED" />
            </Button>
            <ProgressLine
              progress={progress}
              status={'uploading'}
              className="line"
            />
          </ButtonWrapper>
        )
      }
    >
      <Image src={'./static/transfer-menu-image.png'} />
      {!inProgress ? (
        <>
          <Typography
            className="mb-40"
            type="analytics-nav"
            customFont="semi-bold"
            align={'center'}
          >
            <Translate id="CATALOG.TRANSFER_MENU.TITLE" />
          </Typography>
          <MenuTypography className="mb-40">
            <Translate id="CATALOG.TRANSFER_MENU.TEXT" />
          </MenuTypography>
          <Separator />
          <MenuTypography className="mb-40">
            <Translate id="CATALOG.TRANSFER_MENU.SUBTEXT" />
          </MenuTypography>
          <MenuTypography margin="0px">
            <Translate id="CATALOG.TRANSFER_MENU.STEP1" />
          </MenuTypography>
          <SeparatorLine lineDot />
          <MenuTypography margin="0px">
            <Translate id="CATALOG.TRANSFER_MENU.STEP2" />
          </MenuTypography>
          <SeparatorLine lineDot />
          <MenuTypography margin="0px">
            <Translate id="CATALOG.TRANSFER_MENU.STEP3" />
          </MenuTypography>
          <SeparatorLine lineDot />
          <MenuTypography margin="0px">
            <Translate id="CATALOG.TRANSFER_MENU.STEP4" />
          </MenuTypography>
          <SeparatorLine lineDot />
          <MenuTypography margin="0px">
            <Translate id="CATALOG.TRANSFER_MENU.STEP5" />
          </MenuTypography>
          <SeparatorLine lineDot />
          <MenuTypography className="mb-40">
            <Translate id="CATALOG.TRANSFER_MENU.STEP6" />
          </MenuTypography>

          <MenuTypography className="mb-40">
            <Translate id="CATALOG.TRANSFER_MENU.ENDTEXT" />
          </MenuTypography>
        </>
      ) : (
        <>
          <Typography
            className="mb-40"
            type="analytics-nav"
            customFont="semi-bold"
            align={'center'}
          >
            <Translate id="CATALOG.TRANSFER_MENU.TITLE.STARTED" />
          </Typography>
          <Typography className="mb-40">
            <Translate id="CATALOG.TRANSFER_MENU.TEXT.STARTED" />
          </Typography>
          <Separator />
          <Typography className="mb-40">
            <Translate id="CATALOG.TRANSFER_MENU.SUBTEXT.STARTED" />
          </Typography>
          <ul>
            <li>
              <Typography margin="10px">
                <Translate id="CATALOG.TRANSFER_MENU.STARTED.STEP1" />
              </Typography>
            </li>
            <li>
              <Typography margin="10px">
                <Translate id="CATALOG.TRANSFER_MENU.STARTED.STEP2" />
              </Typography>
            </li>
            <li>
              <Typography margin="10px">
                <Translate id="CATALOG.TRANSFER_MENU.STARTED.STEP3" />
              </Typography>
            </li>
            <li>
              <Typography margin="10px">
                <Translate id="CATALOG.TRANSFER_MENU.STARTED.STEP4" />
              </Typography>
            </li>
          </ul>

          <Typography className="mb-40">
            <Translate id="CATALOG.TRANSFER_MENU.STARTED.ENDTEXT" />
          </Typography>
        </>
      )}
    </NewMenu>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CatalogTransferMenu);

const PillButton = styled(Flex)`
  cursor: pointer;
  padding: 12px 18px;
  border-radius: 100px;
  opacity: 1;
  background-color: ${({ theme }) => theme.colors.midGray};
  transition: opacity
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    opacity: 0.75;
  }
`;

const Image = styled.img`
  width: 100%;
  max-width: 248px;
  display: block;
  margin: 0 auto 32px;
`;

const Separator = styled.div`
  height: 1px;
`;

const ButtonWrapper = styled.div`
  .line {
    left: -15px;
    width: 200px;
    margin: 0 auto;
  }
`;

const MenuTypography = styled(Typography)`
  font-size: 14px;
`;
